import axios from 'axios';

export const ADMIN_API_URL =
  process.env.REACT_APP_ADMIN_API_URL || 'http://localhost:5001/api/admin';

const api = axios.create({
  baseURL: ADMIN_API_URL
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const adminLogin = (email, password, rememberMe) =>
  api.post('/auth/login', { email, password, rememberMe });

export const getAdminMe = () => api.get('/auth/me');

export const getStats = () => api.get('/dashboard/stats');
export const getRevenueOverview = () => api.get('/dashboard/revenue');
export const getPendingApprovals = () => api.get('/dashboard/pending-approvals');

export const getExecutiveStats = () => api.get('/executives/stats');
export const listExecutives = () => api.get('/executives');
export const createExecutive = (name, email, executiveCode) =>
  api.post('/executives', { name, email, executiveCode });
export const updateExecutive = (id, name, email, executiveCode) =>
  api.patch(`/executives/${id}`, { name, email, executiveCode });
export const updateExecutiveStatus = (id, status) =>
  api.patch(`/executives/${id}/status`, { status });

export const listVendors = (filters = {}) =>
  api.get('/vendors', { params: filters });
export const createVendor = (
  name,
  ownerName,
  serviceType,
  categoryId,
  plan,
  salesExecutiveId,
  commissionModel,
  listingType,
  isPremium
) =>
  api.post('/vendors', {
    name,
    ownerName,
    serviceType,
    categoryId,
    plan,
    salesExecutiveId,
    commissionModel,
    listingType,
    isPremium
  });

export const updateVendor = (
  id,
  name,
  ownerName,
  serviceType,
  categoryId,
  plan,
  salesExecutiveId,
  commissionModel,
  listingType,
  isPremium
) =>
  api.patch(`/vendors/${id}`, {
    name,
    ownerName,
    serviceType,
    categoryId,
    plan,
    salesExecutiveId,
    commissionModel,
    listingType,
    isPremium
  });
export const updateVendorStatus = (id, status) =>
  api.patch(`/vendors/${id}/status`, { status });
export const deleteVendor = (id) => api.delete(`/vendors/${id}`);
export const listVendorCategories = () => api.get('/vendors/categories');
export const listVendorExecutives = () => api.get('/vendors/executives');

export const listCategories = () => api.get('/categories');
export const createCategory = (name, icon, iconFile = null) => {
  const formData = new FormData();

  formData.append('name', name);

  if (iconFile) {
    formData.append('iconFile', iconFile);
  } else {
    formData.append('icon', icon || 'category');
  }

  return api.post('/categories', formData);
};
export const updateCategory = (id, name, icon) => api.patch(`/categories/${id}`, { name, icon });
export const updateCategoryStatus = (id, isActive) => api.patch(`/categories/${id}/status`, { isActive });
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

export const listServices = (categoryId) => api.get(`/categories/${categoryId}/services`);
export const createService = (categoryId, name, description) =>
  api.post(`/categories/${categoryId}/services`, { name, description });
export const updateService = (id, name, description) => api.patch(`/services/${id}`, { name, description });
export const updateServiceStatus = (id, isActive) => api.patch(`/services/${id}/status`, { isActive });
export const deleteService = (id) => api.delete(`/services/${id}`);

export const listPlans = () => api.get('/subscriptions/plans');
export const createPlan = (name, tierLabel, billingPeriod, price) =>
  api.post('/subscriptions/plans', { name, tierLabel, billingPeriod, price });
export const listSubscriptions = (filters = {}) => api.get('/subscriptions', { params: filters });
export const getSubscriptionLocations = () => api.get('/subscriptions/locations');
export const createSubscription = (vendorId, planId, startDate) =>
  api.post('/subscriptions', { vendorId, planId, startDate });
export const renewSubscription = (id) => api.patch(`/subscriptions/${id}/renew`);
export const updatePlan = (id, name, tierLabel, price) =>
  api.patch(`/subscriptions/plans/${id}`, {
    name,
    tierLabel,
    price
  });

export const getApprovalStats = () => api.get('/approvals/stats');
export const listApprovals = (filters = {}) => api.get('/approvals', { params: filters });
export const approveSubmission = (id, remarks, type = 'vendor_verification') =>
  api.patch(`/approvals/${id}/approve`, { remarks, type });

export const rejectSubmission = (id, remarks, type = 'vendor_verification') =>
  api.patch(`/approvals/${id}/reject`, { remarks, type });
export const reviewDocument = (docId, reviewStatus, remarks) =>
  api.patch(`/approvals/documents/${docId}`, { reviewStatus, remarks });
export const listCommissionRequests = (params = {}) =>
  api.get('/commissions', { params });

export const getCommissionSummary = () =>
  api.get('/commissions/summary');

export const getCommissionEarnings = (params = {}) =>
  api.get('/commissions/earnings', { params });

export const getCommissionRequest = (id) =>
  api.get(`/commissions/${id}`);

export const approveCommissionRequest = (
  id,
  remarks = ''
) =>
  api.patch(`/commissions/${id}/approve`, {
    remarks
  });

export const rejectCommissionRequest = (
  id,
  remarks
) =>
  api.patch(`/commissions/${id}/reject`, {
    remarks
  });

export const markCommissionPaid = (id) =>
  api.patch(`/commissions/${id}/paid`);
export const getReports = (params = {}) =>
  api.get('/reports', { params });


export const listCertificateApplications = (params = {}) =>
  api.get('/certificate-applications', { params });

export const getCertificateApplication = (type, id) =>
  api.get(`/certificate-applications/${type}/${id}`);

export const approveCertificateApplication = (
  type,
  id,
  remarks
) =>
  api.patch(
    `/certificate-applications/${type}/${id}/approve`,
    { remarks }
  );

export const rejectCertificateApplication = (
  type,
  id,
  remarks
) =>
  api.patch(
    `/certificate-applications/${type}/${id}/reject`,
    { remarks }
  );
export const markCertificateSubmitted = (
  type,
  id
) =>
  api.patch(
    `/certificate-applications/${type}/${id}/certificate-status`
  );
  
export const getSettings = () => api.get('/settings');
export const updateSettings = (fields) => api.patch('/settings', fields);
export default api;